import React, { useEffect, useRef, useState } from 'react';
import { Play, Loader, AlertCircle } from 'lucide-react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

/**
 * VideoPlayer
 *
 * Zero-cost-on-mount player for video recordings: renders only a poster
 * <img> (lazy-loaded) and a play overlay. The actual <video> element and
 * the hls.js runtime are mounted on demand when the user clicks play, so
 * the session list in ReviewView doesn't pay for any video bytes until a
 * user actively wants to watch.
 *
 * Inputs: a `recording` object with (at minimum):
 *   - id
 *   - mediaType: 'video'
 *   - status: 'uploading' | 'processing' | 'ready' | 'failed'
 *   - posterPath, hlsPath, storagePath (Storage paths — we resolve to URLs)
 *   - posterUrl, hlsUrl, previewUrl (optional pre-resolved HTTPS URLs)
 *   - userId / ownerUserId (used for live status subscription on the doc)
 *   - recordingDocPath (optional override for the Firestore doc path)
 */
const VideoPlayer = ({ recording, className = '', autoPlayOnLoad = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [resolvedPoster, setResolvedPoster] = useState(
    recording?.posterUrl || null
  );
  const [resolvedHls, setResolvedHls] = useState(recording?.hlsUrl || null);
  const [resolvedPreview, setResolvedPreview] = useState(
    recording?.previewUrl || recording?.url || null
  );
  // Direct-playback URL for the assembled raw .webm. Lets us play the video
  // as soon as assembleRecording finishes, without waiting for transcodeMedia
  // to emit HLS (which can take tens of seconds on top of the assembly).
  const [resolvedRawUrl, setResolvedRawUrl] = useState(null);
  const [liveStatus, setLiveStatus] = useState(recording?.status || null);
  const [liveStoragePath, setLiveStoragePath] = useState(recording?.storagePath || null);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const hlsInstanceRef = useRef(null);

  // Resolve Storage paths to download URLs once (poster only at first, so list
  // rendering only triggers a tiny JPEG fetch — hls manifest is lazy).
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      try {
        if (!resolvedPoster && recording?.posterPath) {
          const url = await getDownloadURL(storageRef(storage, recording.posterPath));
          if (!cancelled) setResolvedPoster(url);
        }
      } catch (e) {
        // Poster not ready yet — that's fine, we'll retry via the live status
        // subscription below.
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [recording?.posterPath, resolvedPoster]);

  // Probe the raw .webm Storage path. The streaming uploader fills in the
  // *expected* path on the session doc before the assembly CF has written
  // the file, so getDownloadURL may 404 the first time. We poll periodically
  // until the file shows up (max ~2 min), then back off.
  useEffect(() => {
    if (resolvedRawUrl) return undefined;
    // Compute `status` inline — reading the derived `status` below would hit
    // the temporal dead zone when this effect's deps array is evaluated.
    const effectiveStatus =
      liveStatus || recording?.status || (recording?.isLocal ? 'uploading' : 'ready');
    if (effectiveStatus === 'ready' || effectiveStatus === 'failed') return undefined;
    const path = liveStoragePath || recording?.storagePath;
    if (!path) return undefined;

    let cancelled = false;
    let timer = null;
    let attempt = 0;

    const tryResolve = async () => {
      if (cancelled) return;
      try {
        const url = await getDownloadURL(storageRef(storage, path));
        if (!cancelled) setResolvedRawUrl(url);
      } catch (e) {
        // 404 while the assembly CF is still running. Retry with a gentle
        // backoff: 3s, 3s, 5s, 5s, 10s, 10s, 15s, cap at 30s, give up at
        // ~2 min total.
        attempt += 1;
        const delay = attempt < 3 ? 3000 : attempt < 5 ? 5000 : attempt < 7 ? 10000 : attempt < 10 ? 15000 : 30000;
        if (attempt < 15) {
          timer = setTimeout(tryResolve, delay);
        }
      }
    };

    tryResolve();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [recording?.storagePath, recording?.status, recording?.isLocal, liveStoragePath, liveStatus, resolvedRawUrl]);

  // Live-subscribe to the recording doc so "Processing…" flips to "Ready"
  // without a manual refresh. Only subscribes when we don't already have a
  // final ready state, to avoid unnecessary reads on finished recordings.
  useEffect(() => {
    if (!recording) return undefined;
    if ((recording.status || liveStatus) === 'ready' && (resolvedPoster || recording.posterUrl)) {
      return undefined;
    }
    const docPath = recording.recordingDocPath;
    if (!docPath) return undefined;
    try {
      const unsub = onSnapshot(doc(db, docPath), async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        setLiveStatus(data.status || null);
        if (data.storagePath) setLiveStoragePath(data.storagePath);
        if (data.posterPath && !resolvedPoster) {
          try {
            const url = await getDownloadURL(storageRef(storage, data.posterPath));
            setResolvedPoster(url);
          } catch (e) { /* noop */ }
        }
        if (data.hlsPath && !resolvedHls) {
          try {
            const url = await getDownloadURL(storageRef(storage, data.hlsPath));
            setResolvedHls(url);
          } catch (e) { /* noop */ }
        }
      }, (err) => {
        console.warn('VideoPlayer live status subscription failed:', err);
      });
      return () => unsub();
    } catch (e) {
      return undefined;
    }
  }, [recording, liveStatus, resolvedPoster, resolvedHls]);

  const status = liveStatus || recording?.status || (recording?.isLocal ? 'uploading' : 'ready');
  // Ready-to-play rules:
  //   - Final `ready` state OR an HLS manifest we can stream OR a legacy
  //     preview URL (audio players used this).
  //   - Plus: once `resolvedRawUrl` is set, the assembled .webm is playable
  //     directly. This unblocks the user the instant assembleRecording
  //     finishes, even if transcodeMedia is still working.
  const isReady = status === 'ready' || !!resolvedHls || !!resolvedPreview || !!resolvedRawUrl;

  // Mount a real <video> only when the user clicks play. hls.js is lazily
  // imported so its ~40KB never ships with the initial bundle.
  useEffect(() => {
    if (!expanded || !videoRef.current) return undefined;
    let cancelled = false;

    const setup = async () => {
      const video = videoRef.current;
      if (!video) return;

      const hlsUrl = resolvedHls || recording?.hlsUrl;
      // Prefer HLS when the transcode pipeline has produced it; otherwise
      // fall through to the direct raw .webm, which is playable as soon as
      // the assembly CF finishes.
      const fallbackUrl = resolvedRawUrl || resolvedPreview || recording?.previewUrl || recording?.url;

      try {
        if (hlsUrl) {
          // Safari supports HLS natively.
          if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = hlsUrl;
          } else {
            const Hls = (await import('hls.js')).default;
            if (cancelled) return;
            if (Hls.isSupported()) {
              const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
              hls.loadSource(hlsUrl);
              hls.attachMedia(video);
              hlsInstanceRef.current = hls;
            } else if (fallbackUrl) {
              video.src = fallbackUrl;
            } else {
              setError('Your browser does not support this video format.');
              return;
            }
          }
        } else if (fallbackUrl) {
          video.src = fallbackUrl;
        } else {
          setError('Video is still processing.');
          return;
        }

        if (autoPlayOnLoad) {
          try { await video.play(); } catch (e) { /* ignore autoplay block */ }
        }
      } catch (e) {
        console.error('VideoPlayer setup error', e);
        setError(e?.message || 'Failed to load video');
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (hlsInstanceRef.current) {
        try { hlsInstanceRef.current.destroy(); } catch (e) { /* noop */ }
        hlsInstanceRef.current = null;
      }
    };
  }, [expanded, resolvedHls, resolvedPreview, resolvedRawUrl, recording, autoPlayOnLoad]);

  const posterSrc = resolvedPoster || recording?.posterUrl;
  // More informative status labels: distinguish "Assembling…" (CF hasn't
  // produced the raw .webm yet) from "Encoding…" (raw playable, HLS cooking).
  const statusLabel = (() => {
    if (status === 'uploading') return 'Uploading…';
    if (status === 'failed') return 'Upload failed';
    if (status === 'ready') return 'Ready';
    if (status === 'processing') {
      return resolvedRawUrl ? 'Encoding…' : 'Assembling…';
    }
    return 'Processing…';
  })();

  return (
    <div className={`relative rounded-lg overflow-hidden bg-black aspect-video ${className}`}>
      {!expanded ? (
        <button
          type="button"
          onClick={() => { if (isReady) setExpanded(true); }}
          className="w-full h-full relative group"
          disabled={!isReady}
          aria-label={isReady ? 'Play video recording' : 'Video processing'}
        >
          {posterSrc ? (
            <img
              src={posterSrc}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-900" />
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition">
            {isReady ? (
              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                <Play size={28} className="text-gray-900 translate-x-0.5" />
              </div>
            ) : status === 'failed' ? (
              <div className="flex flex-col items-center text-white text-sm">
                <AlertCircle size={28} className="mb-1" />
                Upload failed
              </div>
            ) : (
              <div className="flex flex-col items-center text-white text-sm">
                <Loader size={28} className="animate-spin mb-1" />
                {statusLabel}
              </div>
            )}
          </div>
        </button>
      ) : (
        <video
          ref={videoRef}
          controls
          playsInline
          poster={posterSrc || undefined}
          className="w-full h-full"
        />
      )}
      {error && (
        <div className="absolute inset-x-0 bottom-0 bg-red-600/90 text-white text-xs px-2 py-1">
          {error}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
